import React, {FC, useEffect, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import PositionReport from './PositionReportNew'
import {OperationsList} from './OperationsList'
import {useAuth} from '../../auth'
import {OP} from '../core/operationPermissions'
import {useOperationsAccess} from '../core/useOperationsAccess'
import {useOperationsScopeUx} from '../core/operationsScopeUx'
import {getSubmittedReportsFull} from '../core/_requests'

const getQuarterBoundaryMidnightMs = (year: number) => [
  new Date(year, 3, 1, 0, 0, 0, 0).getTime(),
  new Date(year, 6, 1, 0, 0, 0, 0).getTime(),
  new Date(year, 9, 1, 0, 0, 0, 0).getTime(),
  new Date(year + 1, 0, 1, 0, 0, 0, 0).getTime(),
].filter((time) => !Number.isNaN(time))

const formatBoundaryLabel = (timeMs: number) => {
  const date = new Date(timeMs)
  const pad = (value: number) => String(value).padStart(2, '0')
  const day = pad(date.getDate())
  const month = date.toLocaleString('en-GB', {month: 'short'})
  const year = date.getFullYear()
  return `${day} ${month} ${year} 00:00`
}

const boundaryMatchesReport = (report: any, boundaryMs: number) => {
  const values = Array.isArray(report?.values) ? report.values : []
  const matches = values.filter((value: any) => {
    const label = String(value?.field?.label || value?.label || '').toLowerCase()
    return (
      label.includes('report date time') ||
      label.includes('departure date time') ||
      label.includes('departure datetime') ||
      label.includes('arrival date time') ||
      label.includes('arrival datetime')
    )
  })

  return matches.some((match: any) => {
    const time = Date.parse(match?.valueText || '')
    return Number.isFinite(time) && time === boundaryMs
  })
}

const QuarterReminderModal: FC<{open: boolean; onClose: () => void; midnightLabel: string}> = ({
  open,
  onClose,
  midnightLabel,
}) => {
  if (!open) return null
  return (
    <div
      className='modal fade show'
      style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.35)'}}
      role='dialog'
      aria-modal='true'
    >
      <div className='modal-dialog modal-dialog-centered modal-sm'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Quarter-End Report Reminder</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={onClose} />
          </div>
          <div className='modal-body'>
            <p className='mb-2'>
              Please submit the quarter-end position report for the boundary time below.
            </p>
            {midnightLabel && (
              <div className='fw-bold text-primary'>{midnightLabel}</div>
            )}
            <div className='text-muted mt-2' style={{fontSize: '0.85rem'}}>
              This helps CII split events exactly at quarter boundaries.
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * One page with two tabs:
 * - View 1: PositionReportNew
 * - View 2: OperationsList
 *
 * Uses ?tab=view1|view2 to deep-link the active tab.
 */
const PositionReportsTabs: FC = () => {
  const [params, setParams] = useSearchParams()
  const activeTab = (params.get('tab') === 'view2' ? 'view2' : 'view1') as 'view1' | 'view2'
  const [mountedTabs, setMountedTabs] = useState<Set<'view1' | 'view2'>>(() => new Set([activeTab]))

  const setActive = (tab: 'view1' | 'view2') => {
    const next = new URLSearchParams(params)
    next.set('tab', tab)
    setParams(next, {replace: true})
  }

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev
      const next = new Set(prev)
      next.add(activeTab)
      return next
    })
  }, [activeTab])

  // Optional: role-based actions (e.g., show Create Report when View 2 is active)
  const {currentUser} = useAuth() as any
  const {opCan, permissionsLoaded, isSuperAdmin} = useOperationsAccess()
  const scopeUx = useOperationsScopeUx(isSuperAdmin)
  const isCrew = scopeUx.isCrew
  const [showQuarterReminder, setShowQuarterReminder] = useState(false)
  const [midnightLabel, setMidnightLabel] = useState('')

  useEffect(() => {
    let active = true
    const loadReminderState = async () => {
      if (!isCrew || !currentUser?.vessel?.id) {
        if (active) setShowQuarterReminder(false)
        return
      }

      const now = new Date()
      const year = now.getFullYear()
      const boundaries = [
        ...getQuarterBoundaryMidnightMs(year - 1),
        ...getQuarterBoundaryMidnightMs(year),
      ]
        .filter((time) => time <= now.getTime())
        .sort((a, b) => a - b)

      if (!boundaries.length) {
        if (active) setShowQuarterReminder(false)
        return
      }

      const latestBoundary = boundaries[boundaries.length - 1]
      const from = `${year - 1}-01-01`
      const to = `${year}-12-31`
      const reports = await getSubmittedReportsFull(from, to, currentUser.vessel.id).catch(() => [])
      const hasBoundaryReport = (reports || []).some((report: any) =>
        boundaryMatchesReport(report, latestBoundary)
      )

      if (active) {
        setShowQuarterReminder(!hasBoundaryReport)
        setMidnightLabel(formatBoundaryLabel(latestBoundary))
      }
    }

    loadReminderState()
    return () => {
      active = false
    }
  }, [isCrew, currentUser?.vessel?.id])

  if (permissionsLoaded && !opCan(OP.READ_POSITION_REPORT)) {
    return (
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='alert alert-warning m-5'>You do not have permission to perform this action.</div>
      </div>
    )
  }

  // === NON-CREW: keep tabs behavior ===
  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main' style={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: 'calc(100dvh - 150px)', maxHeight: 'calc(100dvh - 150px)', overflow: 'hidden'}}>
      <div className='d-flex flex-column flex-column-fluid' style={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'}}>
        <div id='kt_app_content' className='app-content flex-column-fluid d-flex flex-column' style={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'}}>
          <div className='card flex-column-fluid d-flex flex-column' style={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', borderRadius: 0}}>
            <div className='px-5 py-3 border-bottom d-flex align-items-center justify-content-between bg-white'>
              <h3 className='card-title fw-bold text-dark m-0'>Position Reports</h3>

              {/* Tabs on the top right */}
              <div className='d-flex align-items-center gap-3'>
                {opCan(OP.CREATE_POSITION_REPORT) && activeTab === 'view2' && (
                  <button
                    className='btn btn-sm btn_primary'
                    onClick={() => document.dispatchEvent(new CustomEvent('opslist-open-create-modal'))}
                  >
                    Create report
                  </button>
                )}
                <div className='btn-group btn-group-sm' role='group' aria-label='View switch'>
                  <button
                    type='button'
                    className={`btn btn-sm ${activeTab === 'view1' ? 'btn_primary' : 'btn-light border border-secondary'}`}
                    onClick={() => setActive('view1')}
                    title='Position Report New'
                  >
                    View 1
                  </button>
                  <button
                    type='button'
                    className={`btn btn-sm ${activeTab === 'view2' ? 'btn_primary' : 'btn-light border border-secondary'}`}
                    onClick={() => setActive('view2')}
                    title='Operations List'
                  >
                    View 2
                  </button>
                </div>
              </div>
            </div>

            {/* Body swaps with lazy mounting & smooth transition */}
            <div style={{flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative'}}>
              {mountedTabs.has('view1') && (
                <div style={{display: activeTab === 'view1' ? 'block' : 'none', height: '100%'}}>
                  <PositionReport embedded />
                </div>
              )}
              {mountedTabs.has('view2') && (
                <div style={{display: activeTab === 'view2' ? 'block' : 'none', height: '100%'}}>
                  <OperationsList embedded />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PositionReportsTabs
